import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Plus, Trash2, Undo2, Redo2, Download, Printer, RefreshCw, Loader2, Check, Keyboard } from "lucide-react";
import { SheetGrid } from "@/components/production/SheetGrid";
import { DispatchPanel } from "@/components/production/DispatchPanel";
import { ScratchpadWorkspace } from "@/components/production/ScratchpadWorkspace";
import { SheetContextEditor } from "@/components/production/SheetContextEditor";
import { SECTIONS, columnsFor, newRow, rowSqft, STATUS } from "@/components/production/sheetModel";
import { reconcileDerivedDoors } from "@/components/production/scratchpadModel";
import { GeneralProductionDispatchDialog } from "@/components/production/GeneralProductionDispatchDialog";
import { ProductionSafetyPanel } from "@/components/production/ProductionSafetyPanel";
import { SectionDispatchBadge } from "@/components/production/SectionDispatchBadge";
import { SendToProductionButton } from "@/components/production/SendToProductionButton";

const MANAGER_ROLES = ["owner", "project_manager", "office_admin"];

export default function ProductionSheetPage() {
  const { sheetId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const canEdit = MANAGER_ROLES.includes(user?.role);
  const canRelease = ["owner", "project_manager"].includes(user?.role);
  const [sheet, setSheet] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [tab, setTab] = useState("doors_fronts");
  const [selected, setSelected] = useState(new Set());
  const [saveState, setSaveState] = useState("saved");
  const [showKeys, setShowKeys] = useState(false);
  const [showGeneralDispatch, setShowGeneralDispatch] = useState(false);
  const hist = useRef({ past: [], future: [] });
  const timer = useRef(null);
  const saveVersion = useRef(0);

  useEffect(() => {
    axios.get(`${API}/production-sheets/${sheetId}`).then(r => setSheet(r.data)).catch(() => { toast.error("Sheet not found"); navigate("/production-sheets"); });
    axios.get(`${API}/suppliers`).then(r => setSuppliers(r.data || [])).catch(() => {});
  }, [sheetId, navigate]);

  const persist = useCallback((sections, title, extra = {}) => {
    clearTimeout(timer.current);
    const version = ++saveVersion.current;
    setSaveState("saving");
    timer.current = setTimeout(async () => {
      try {
        const r = await axios.put(`${API}/production-sheets/${sheetId}`, { sections, ...(title !== undefined ? { title } : {}), ...extra });
        if (version !== saveVersion.current) return;
        setSheet(r.data);
        setSaveState("saved");
      } catch (e) {
        if (version !== saveVersion.current) return;
        setSaveState("error");
        toast.error(e.response?.data?.detail || "Save failed");
      }
    }, 700);
  }, [sheetId]);

  const setSections = (next, { record = true } = {}) => {
    const resolved = sheet?.mode === "scratchpad" ? reconcileDerivedDoors(next) : next;
    setSheet(prev => {
      if (record) { hist.current.past = [...hist.current.past.slice(-49), prev.sections]; hist.current.future = []; }
      return { ...prev, sections: resolved };
    });
    persist(resolved);
  };
  const undo = () => { const p = hist.current.past.pop(); if (!p) return; hist.current.future.push(sheet.sections); setSections(p, { record: false }); };
  const redo = () => { const f = hist.current.future.pop(); if (!f) return; hist.current.past.push(sheet.sections); setSections(f, { record: false }); };

  const rowsChange = (key) => (rows) => setSections({ ...sheet.sections, [key]: rows });
  const setScratchpadSettings = (settings) => {
    setSheet(prev => ({ ...prev, scratchpad_settings: settings }));
    persist(sheet.sections, sheet.title, { scratchpad_settings: settings });
  };
  const addRow = () => setSections({ ...sheet.sections, [tab]: [...(sheet.sections[tab] || []), newRow(tab)] });
  const deleteSelected = () => {
    const next = Object.fromEntries(SECTIONS.map(s => [s.key, (sheet.sections[s.key] || []).filter(r => !selected.has(r.row_id))]));
    setSections(next); setSelected(new Set());
  };
  const toggleRow = (id, on) => setSelected(prev => { const n = new Set(prev); on ? n.add(id) : n.delete(id); return n; });
  const toggleAll = (key) => (on) => setSelected(prev => { const n = new Set(prev); (sheet.sections[key] || []).forEach(r => on ? n.add(r.row_id) : n.delete(r.row_id)); return n; });
  const reimport = async () => {
    try { const r = await axios.post(`${API}/production-sheets/${sheetId}/import-estimate`); setSheet(r.data.sheet); toast.success(r.data.added ? `${r.data.added} new item(s) imported from ${sheet.estimate_number}` : "Sheet already has every estimate item"); }
    catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
  };
  const replaceSheetState = updated => {
    clearTimeout(timer.current); saveVersion.current += 1; hist.current = { past: [], future: [] };
    setSelected(new Set()); setSheet(updated); setSaveState("saved");
  };

  if (!sheet) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>;
  const supplierNames = Object.fromEntries(suppliers.map(s => [s.supplier_id, s.company_name]));
  const rows = sheet.sections[tab] || [];
  const totals = SECTIONS.map(s => { const rs = sheet.sections[s.key] || []; return { ...s, rows: rs.length, qty: rs.reduce((a, r) => a + (Number(r.quantity) || 0), 0), sqft: rs.reduce((a, r) => a + rowSqft(r), 0) }; });
  const st = STATUS[sheet.status] || STATUS.draft;
  const packageHistory = sheet.general_production_dispatches || [];
  const approvedPackage = [...packageHistory].reverse().find(item => item.approval_status === "approved" && item.immutable === true);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="production-sheet-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono text-slate-500">{sheet.sheet_number}</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${st.cls}`} data-testid="sheet-status">{sheet.mode === "scratchpad" ? "Scratchpad Draft" : st.label}</span>
              <span className="text-xs text-slate-400 flex items-center gap-1" data-testid="save-state">
                {saveState === "saving" ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</> : saveState === "error" ? <span className="text-red-500">Not saved</span> : <><Check className="w-3 h-3 text-emerald-600" /> Saved</>}
              </span>
            </div>
            <Input value={sheet.title || ""} disabled={!canEdit} onChange={e => { setSheet({ ...sheet, title: e.target.value }); persist(sheet.sections, e.target.value); }}
              className="mt-1 h-9 text-lg font-bold border-transparent hover:border-slate-200 focus:border-slate-300 px-1 -ml-1 w-full max-w-md" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="sheet-title-input" />
            {sheet.mode === "scratchpad" ? (
              <SheetContextEditor sheet={sheet} canEdit={canEdit} onSaved={setSheet} />
            ) : (
              <p className="text-sm text-slate-500 truncate">{sheet.customer_name || "No customer"}{sheet.project_name ? ` · ${sheet.project_name}` : ""}{sheet.estimate_number ? ` · from ${sheet.estimate_number}` : ""}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canRelease && <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowGeneralDispatch(true)} disabled={saveState === "saving"} data-testid="approve-for-shop-floor-button">Approve for Shop Floor</Button>}
          {canRelease && approvedPackage && sheet.project_id && <SendToProductionButton projectId={sheet.project_id} packageId={approvedPackage.file_id} packageVersion={approvedPackage.version} packageLabel={`${sheet.sheet_number} · ${sheet.title}`} size="sm" testId="send-approved-sheet-to-cnc-button" />}
          <Button variant="outline" size="sm" onClick={() => setShowKeys(v => !v)} data-testid="toggle-shortcuts"><Keyboard className="w-4 h-4 mr-1" /> Shortcuts</Button>
          {sheet.mode !== "scratchpad" && sheet.estimate_id && canEdit && <Button variant="outline" size="sm" onClick={reimport} data-testid="reimport-estimate-btn"><RefreshCw className="w-4 h-4 mr-1" /> Import from estimate</Button>}
          <Button variant="outline" size="sm" onClick={() => window.open(`${API}/production-sheets/${sheetId}/pdf?download=true`, "_blank", "noopener,noreferrer")} data-testid="download-production-sheet-pdf-button"><Download className="w-4 h-4 mr-1" />Download PDF</Button>
          <Button variant="outline" size="sm" onClick={() => window.open(`${API}/production-sheets/${sheetId}/pdf`, "_blank", "noopener,noreferrer")} data-testid="print-production-sheet-button"><Printer className="w-4 h-4 mr-1" />Print</Button>
        </div>
      </div>

      {showKeys && (
        <div className="text-xs text-slate-600 rounded-lg border bg-slate-50 p-3 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1" data-testid="shortcuts-help">
          <span><b>Drag</b> cells to select · <b>Shift+click</b> extend</span><span><b>Drag the blue square</b> to fill down/up (series or pattern)</span>
          <span><b>Ctrl+C / X / V</b> copy, cut, paste (works with Excel/Sheets)</span><span><b>Ctrl+D</b> fill down · <b>Del</b> clear · <b>Ctrl+Z / Y</b> undo, redo</span>
          <span><b>Enter / F2</b> edit · <b>Tab</b> next cell · type to overwrite</span><span><b>Right-click</b> for insert / duplicate / delete rows</span>
          {sheet.mode === "scratchpad" ? <><span>Box Types derive doors until dimensions are manually overridden</span><span>Hardware groups DOOR hinges and DRAWER / DRW BX Salice slides automatically</span></> : <><span>Maple → stained, 2-face (locked)</span><span>MDF → painted, choose 1- or 2-face · HPL/Thermofoil have no faces</span></>}
        </div>
      )}

      {sheet.mode === "scratchpad" ? (
        <ScratchpadWorkspace
          sheet={sheet}
          canEdit={canEdit}
          onSectionsChange={setSections}
          onSettingsChange={setScratchpadSettings}
          onUndo={undo}
          onRedo={redo}
          suppliers={suppliers}
          setSuppliers={setSuppliers}
          onSheetUpdate={setSheet}
          dirty={saveState === "saving"}
        />
      ) : <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4 items-start">
        <div className="space-y-3 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-1 border-b w-full sm:w-auto" role="tablist">
              {totals.map(t => (
                <button key={t.key} role="tab" onClick={() => setTab(t.key)} className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-700"}`} data-testid={`section-tab-${t.key}`}>
                  <span>{t.label} <span className="ml-1 text-xs text-slate-400">{t.rows}</span></span><SectionDispatchBadge sectionId={t.key} status={sheet.shop_floor_section_status} compact />
                </button>
              ))}
            </div>
            {canEdit && (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={undo} title="Undo (Ctrl+Z)" data-testid="undo-btn"><Undo2 className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={redo} title="Redo (Ctrl+Y)" data-testid="redo-btn"><Redo2 className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" className="h-8" onClick={addRow} data-testid="add-row-btn"><Plus className="w-4 h-4 mr-1" /> Row</Button>
                <Button variant="outline" size="sm" className="h-8 text-red-600" disabled={!selected.size} onClick={deleteSelected} data-testid="delete-selected-btn"><Trash2 className="w-4 h-4 mr-1" /> {selected.size || ""}</Button>
              </div>
            )}
          </div>
          <SheetGrid key={tab} section={tab} columns={columnsFor(tab)} rows={rows} onRowsChange={rowsChange(tab)} onUndo={undo} onRedo={redo}
            selectedIds={selected} onToggleRow={toggleRow} onToggleAll={toggleAll(tab)} supplierNames={supplierNames} readOnly={!canEdit} />
          <div className="flex flex-wrap gap-4 text-xs text-slate-600" data-testid="section-totals">
            {totals.map(t => <span key={t.key}><b>{t.short}:</b> {t.qty} pcs{t.key !== "cabinet_boxes" ? ` · ${t.sqft.toFixed(2)} paint sqft` : ""}</span>)}
            <span className="ml-auto text-slate-400">Paint sqft = ((W+2)×(H+2))÷144 × qty × faces</span>
          </div>
        </div>
        <DispatchPanel sheet={sheet} suppliers={suppliers} setSuppliers={setSuppliers} selectedIds={selected} canDispatch={canEdit} dirty={saveState === "saving"}
          onDispatched={(s) => { setSheet(s); setSelected(new Set()); }} />
      </div>}
      <ProductionSafetyPanel sheet={sheet} canManage={canEdit} dirty={saveState === "saving"} onSheetReplaced={replaceSheetState} />
      <GeneralProductionDispatchDialog open={showGeneralDispatch} onOpenChange={setShowGeneralDispatch} sheet={sheet} dirty={saveState === "saving"} onDispatched={setSheet} />
    </div>
  );
}
